<template>
  <div class="modal-backdrop">
    <div class="modal">
      <div class="modal-header">
        <h2>{{ $t('bo.billing.adjustTitle') }}</h2>
        <button @click="$emit('close')" class="btn-close">✕</button>
      </div>

      <form @submit.prevent="submit">
        <div class="field">
          <label>{{ $t('bo.billing.adjustAmount') }}</label>
          <input v-model.number="form.amount" type="number" min="0.01" step="0.01" required class="input" />
        </div>

        <div class="field">
          <label>{{ $t('bo.billing.adjustType') }}</label>
          <select v-model="form.type" class="input">
            <option value="adjustment">{{ $t('bo.billing.typeAdjustment') }}</option>
            <option value="refund">{{ $t('bo.billing.typeRefund') }}</option>
          </select>
        </div>

        <div class="field">
          <label>{{ $t('bo.billing.adjustDescription') }}</label>
          <textarea v-model="form.description" required maxlength="500" rows="3" class="input" />
        </div>

        <div v-if="error" class="error-msg">{{ error }}</div>
        <div v-if="success" class="success-msg">{{ success }}</div>

        <div class="modal-footer">
          <button type="button" @click="$emit('close')" class="btn-cancel">{{ $t('bo.common.cancel') }}</button>
          <button type="submit" :disabled="saving" class="btn-submit">
            {{ saving ? $t('bo.common.saving') : $t('bo.billing.adjustConfirm') }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import billingService from '@/services/billingService'

const props = defineProps({ companyId: { type: Number, required: true } })
const emit = defineEmits(['close', 'adjusted'])
const { t } = useI18n()

const form = ref({ amount: null, type: 'adjustment', description: '' })
const saving = ref(false)
const error = ref('')
const success = ref('')

const submit = async () => {
  saving.value = true
  error.value = ''
  success.value = ''
  try {
    const res = await billingService.adjustCredits(props.companyId, form.value)
    success.value = t('bo.billing.adjustSuccess', { balance: res.data?.data?.new_balance })
    setTimeout(() => emit('adjusted'), 1200)
  } catch (e) {
    error.value = e.response?.data?.message || t('bo.billing.adjustFailed')
  } finally {
    saving.value = false
  }
}
</script>

<style scoped>
.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 300;
}

.modal {
  background: white;
  border-radius: 0.75rem;
  width: 100%;
  max-width: 480px;
  padding: 1.5rem;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.25rem;
}

.modal-header h2 {
  font-size: 1.1rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
}

.btn-close { background: none; border: none; font-size: 1rem; color: #6b7280; cursor: pointer; }

.field { margin-bottom: 1rem; }

.field label {
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.375rem;
}

.input {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  box-sizing: border-box;
}

.input:focus { outline: none; border-color: #3b82f6; }

.error-msg { color: #dc2626; font-size: 0.875rem; margin-bottom: 0.75rem; }
.success-msg { color: #059669; font-size: 0.875rem; margin-bottom: 0.75rem; }

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1.25rem;
}

.btn-cancel {
  padding: 0.5rem 1rem;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  cursor: pointer;
}

.btn-cancel:hover { background: #f9fafb; }

.btn-submit {
  padding: 0.5rem 1.25rem;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
}

.btn-submit:disabled { opacity: 0.6; cursor: not-allowed; }
.btn-submit:hover:not(:disabled) { background: #2563eb; }
</style>
